import {
  DataTable,
  EmptyState,
  ProblemNotice,
  StatusBadge,
  type DataTableColumn,
  type StatusVariant,
} from "@quovy/ui";
import { Button } from "@quovy/ui/button";
import { Logotype } from "@quovy/ui/logo";
import { Monitor, Moon, Plus, Sun } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AgentTokenDialog } from "./components/agent-token-dialog";
import { RegisterEnvironmentDialog } from "./components/register-environment-dialog";
import { SignIn } from "./components/sign-in";
import {
  ApiError,
  portal,
  SessionExpired,
  type AgentTokenResult,
  type CreatePortalEnvironmentInput,
  type CreatePortalEnvironmentResult,
  type PortalEnvironment,
  type PortalMe,
} from "./lib/api";
import { useTheme } from "./lib/theme";

type LoadState = "pending" | "ready" | "failed" | "signed-out";

function statusVariant(environment: PortalEnvironment): StatusVariant {
  if (environment.status === "disabled") return "neutral";
  if (environment.blocked) return "warning";
  if (environment.status === "paused") return "info";
  return "success";
}

function statusLabel(environment: PortalEnvironment): string {
  if (environment.status === "disabled") return "Retired";
  if (environment.blocked) return "Blocked";
  if (environment.status === "paused") return "Paused";
  return "Active";
}

function environmentFromCreate(
  input: CreatePortalEnvironmentInput,
  result: CreatePortalEnvironmentResult,
): PortalEnvironment {
  return {
    public_id: result.public_id,
    slug: result.environment,
    display_name: input.display_name,
    cloud: input.cloud,
    region: input.region,
    blocked: result.blocked,
    status: "active",
  };
}

export function App() {
  const { theme, cycle } = useTheme();
  const [load, setLoad] = useState<LoadState>("pending");
  const [session, setSession] = useState<PortalMe | null>(null);
  const [environments, setEnvironments] = useState<PortalEnvironment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<"list" | "logout">("list");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [mintTarget, setMintTarget] = useState<PortalEnvironment | null>(null);

  const expireSession = useCallback(() => {
    setSession(null);
    setEnvironments([]);
    setRegisterOpen(false);
    setMintTarget(null);
    setError(null);
    setErrorKind("list");
    setLoad("signed-out");
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const me = await portal.me();
      setSession(me);
      const listed = await portal.environments();
      setEnvironments(listed.environments);
      setError(null);
      setErrorKind("list");
      setLoad("ready");
    } catch (cause) {
      if (cause instanceof SessionExpired) {
        expireSession();
        return;
      }
      setError(cause instanceof ApiError ? cause.message : String(cause));
      setErrorKind("list");
      setLoad((current) => (current === "ready" ? "ready" : "failed"));
    }
  }, [expireSession]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    document.title = session ? `${session.organization.name} - Quovy portal` : "Quovy portal";
  }, [session]);

  const createEnvironment = useCallback(
    async (input: CreatePortalEnvironmentInput) => {
      let result: CreatePortalEnvironmentResult;
      try {
        result = await portal.createEnvironment(input);
      } catch (cause) {
        if (cause instanceof SessionExpired) expireSession();
        throw cause;
      }
      try {
        const listed = await portal.environments();
        setEnvironments(listed.environments);
        setError(null);
      } catch (cause) {
        // Create already committed. A list failure must not hide the shown-once
        // trust policy or next steps; mint can still expire the session.
        setEnvironments((current) => {
          if (current.some((row) => row.public_id === result.public_id)) return current;
          return [...current, environmentFromCreate(input, result)];
        });
        setErrorKind("list");
        setError(cause instanceof ApiError ? cause.message : String(cause));
      }
      return result;
    },
    [expireSession],
  );

  const mintAgentToken = useCallback(
    async (environment: { id: string; slug: string }) => {
      try {
        const result: AgentTokenResult = await portal.mintAgentToken(environment.id);
        return result;
      } catch (cause) {
        if (cause instanceof SessionExpired) expireSession();
        throw cause;
      }
    },
    [expireSession],
  );

  const signOut = useCallback(async () => {
    try {
      await portal.logout();
      expireSession();
    } catch (cause) {
      if (cause instanceof SessionExpired) {
        expireSession();
        return;
      }
      try {
        await portal.me();
        setErrorKind("logout");
        setError(cause instanceof ApiError ? cause.message : String(cause));
      } catch (probe) {
        if (probe instanceof SessionExpired) {
          expireSession();
          return;
        }
        setErrorKind("logout");
        setError(cause instanceof ApiError ? cause.message : String(cause));
      }
    }
  }, [expireSession]);

  const columns = useMemo<DataTableColumn<PortalEnvironment>[]>(
    () => [
      {
        key: "display_name",
        label: "Environment",
        minWidth: "220px",
        mobilePrimary: true,
        render: (_value, environment) => (
          <div className="min-w-0">
            <strong className="block truncate text-sm font-semibold">
              {environment.display_name}
            </strong>
            <span className="mt-0.5 block truncate font-mono text-xs text-muted-foreground">
              {environment.slug}
            </span>
          </div>
        ),
      },
      {
        key: "cloud",
        label: "Cloud",
        minWidth: "140px",
        hideBelow: 640,
        render: (_value, environment) => (
          <span className="font-mono text-xs">
            {environment.cloud.toUpperCase()} · {environment.region}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        minWidth: "140px",
        mobileSecondary: true,
        render: (_value, environment) => (
          <div className="grid gap-1">
            <StatusBadge variant={statusVariant(environment)}>
              {statusLabel(environment)}
            </StatusBadge>
            {environment.blocked ? (
              <span className="text-xs text-muted-foreground">{environment.blocked}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: "public_id",
        label: "Token",
        minWidth: "140px",
        hideable: false,
        render: (_value, environment) => (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              setMintTarget(environment);
            }}
          >
            Issue token
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="cp-app-shell">
      <header className="cp-commandbar">
        <div className="cp-wordmark">
          <Logotype height={18} className="cp-quovy-logo" />
          <span>portal</span>
        </div>
        <div className="cp-commandbar-actions">
          {session ? (
            <span className="portal-identity">
              {session.user.email} · {session.organization.name}
            </span>
          ) : null}
          {session ? (
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={cycle}
            className="cp-theme-button"
            aria-label={`Theme: ${theme}. Switch theme.`}
          >
            {theme === "light" ? (
              <Sun size={16} aria-hidden="true" />
            ) : theme === "dark" ? (
              <Moon size={16} aria-hidden="true" />
            ) : (
              <Monitor size={16} aria-hidden="true" />
            )}
          </Button>
        </div>
      </header>

      {load === "pending" ? (
        <main className="cp-console">
          <p className="text-sm text-muted-foreground">Loading portal…</p>
        </main>
      ) : null}

      {load === "failed" ? (
        <main className="cp-console">
          <ProblemNotice
            className="cp-banner"
            title="Portal could not load"
            howToResolve="Retry the portal session, or ask a Quovy operator if identity is still being configured."
            resolution={{
              kind: "action",
              node: (
                <Button variant="outline" size="sm" onClick={() => void loadSession()}>
                  Retry
                </Button>
              ),
            }}
            live
          >
            {error ?? "The portal did not return a session."}
          </ProblemNotice>
        </main>
      ) : null}

      {load === "signed-out" ? <SignIn onSignedIn={() => void loadSession()} /> : null}

      {load === "ready" && session ? (
        <main className="cp-console">
          <div className="cp-page-intro">
            <div>
              <span className="cp-eyebrow">{session.organization.name}</span>
              <h1>Environments</h1>
            </div>
            <div className="cp-page-intro-actions">
              <Button onClick={() => setRegisterOpen(true)}>
                <Plus size={16} aria-hidden="true" />
                Register environment
              </Button>
            </div>
          </div>

          {error ? (
            <ProblemNotice
              className="cp-banner"
              title={
                errorKind === "logout"
                  ? "Sign-out did not complete"
                  : "Environments could not be refreshed"
              }
              howToResolve={
                errorKind === "logout"
                  ? "Retry Sign out. This browser cannot clear the session cookie until the portal answers."
                  : "Retry the environments list."
              }
              resolution={{
                kind: "action",
                node:
                  errorKind === "logout" ? (
                    <Button variant="outline" size="sm" onClick={() => void signOut()}>
                      Sign out
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => void loadSession()}>
                      Retry
                    </Button>
                  ),
              }}
              live
            >
              {error}
            </ProblemNotice>
          ) : null}

          <DataTable
            data={environments}
            columns={columns}
            getRowKey={(row) => row.public_id}
            emptyState={
              <EmptyState
                title="No environments yet"
                action={<Button onClick={() => setRegisterOpen(true)}>Register environment</Button>}
              />
            }
          />

          <RegisterEnvironmentDialog
            open={registerOpen}
            onOpenChange={setRegisterOpen}
            onCreate={createEnvironment}
            onMint={mintAgentToken}
          />
          {mintTarget ? (
            <AgentTokenDialog
              open
              environment={{ id: mintTarget.public_id, slug: mintTarget.slug }}
              mode="replace"
              onOpenChange={(next) => {
                if (!next) setMintTarget(null);
              }}
              onMint={mintAgentToken}
            />
          ) : null}
        </main>
      ) : null}
    </div>
  );
}
